import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import { doctorAPI } from '../../services/api';

const PatientProfileView = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPatient = async () => {
      try {
        const res = await doctorAPI.getPatientDetail(uid);
        setPatient(res.data || null);
      } catch (error) {
        toast.error(error.message || 'Unable to load patient profile.');
      } finally {
        setLoading(false);
      }
    };

    loadPatient();
  }, [uid]);

  if (loading) {
    return (
      <Layout title="Patient Profile" role="doctor" showBack>
        <div className="p-4 space-y-4">
          {[1, 2, 3].map((item) => <div key={item} className="skeleton h-24 rounded-2xl" />)}
        </div>
      </Layout>
    );
  }

  if (!patient) {
    return (
      <Layout title="Patient Profile" role="doctor" showBack>
        <div className="p-4">
          <div className="card text-center py-10 text-gray-500">
            Patient profile is unavailable.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Patient Profile" role="doctor" showBack>
      <div className="p-4 pb-24 space-y-4">
        <div className="card flex items-center gap-4">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-primary-700 text-2xl font-bold">
              {(patient.full_name || 'P').charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate">{patient.full_name || 'Patient'}</h2>
            <p className="text-sm text-gray-500">{patient.email || 'No email available'}</p>
          </div>
        </div>

        <div className="card space-y-4">
          <h3 className="font-semibold text-gray-900">Patient Details</h3>
          {[
            ['Phone Number', patient.phone_number],
            ['Date of Birth', patient.date_of_birth],
            ['Gender', patient.gender],
            ['County', patient.county],
            ['Sub-County', patient.sub_county],
            ['Blood Type', patient.blood_type],
          ].map(([label, value]) => (
            <div key={label}>
              <label className="label">{label}</label>
              <p className="text-gray-900 break-words">{value || '—'}</p>
            </div>
          ))}
        </div>

        <div className="card space-y-3">
          <h3 className="font-semibold text-gray-900">Chronic Conditions</h3>
          {(patient.chronic_conditions || []).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {patient.chronic_conditions.map((condition) => (
                <span key={condition} className="px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">
                  {condition}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No chronic conditions recorded.</p>
          )}
        </div>

        <div className="card space-y-3">
          <h3 className="font-semibold text-gray-900">Allergies</h3>
          {(patient.allergies || []).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {patient.allergies.map((allergy) => (
                <span key={allergy} className="px-3 py-1 bg-red-50 text-red-700 text-sm rounded-full">
                  {allergy}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No allergies recorded.</p>
          )}
        </div>

        <div className="card space-y-3">
          <h3 className="font-semibold text-gray-900">Emergency Contact</h3>
          <div>
            <label className="label">Name</label>
            <p className="text-gray-900">{patient.emergency_contact_name || '—'}</p>
          </div>
          <div>
            <label className="label">Phone Number</label>
            <p className="text-gray-900">{patient.emergency_contact_phone || '—'}</p>
          </div>
        </div>

        <button
          onClick={() => navigate('/doctor/queue')}
          className="btn-secondary w-full"
        >
          Back to Queue
        </button>
      </div>
    </Layout>
  );
};

export default PatientProfileView;
